export type FontWeight = 'light' | 'normal' | 'medium' | 'semibold' | 'bold';
export type FontSize = 'tiny' | 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
export type FontStyle = 'normal' | 'italic';
export type Radius = 'full' | 'xxl' | 'xl' | 'sm' | 'md' | 'lg' | 'none' | 'default';
export type Shadows = 'sm' | 'md' | 'lg' | 'xl' | 'pya' | 'input' | 'table';
export type Screen = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl';
export type Spacing = 'base' | 'none' | 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
export type ZIndex = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'alert';
export type BorderColors = 'light' | 'main' | 'dark' | 'pya' | 'sst';
export type GreyColors = 5 | 10 | 20 | 30 | 40 | 50 | 60 | 70 | 80 | 90;
export type SocialColors = 'facebook' | 'twitter' | 'linkedIn' | 'github';
export type DashboardColors = 'icon';
export type ChipColors = 'green' | 'yellow' | 'red' | 'gray' | 'info';
export type TableColors = 'text';
export type GeneralColors =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'info'
  | 'error'
  | 'link'
  | 'body'
  | 'white'
  | 'text'
  | 'textSecondary'
  | 'disabled'
  | 'transparent'
  | 'black'
  | 'purple'
  | 'placeholder'
  | 'logo'
  | 'sst'
  | 'sstTitle';

interface IFont {
  family: string;
  weight: { [key in FontWeight]: string };
  size: { [key in FontSize]: string };
}
type IShadows = { [key in Shadows]: string };
type IScreen = { [key in Screen]: string };
type ISpacing = { [key in Spacing]: string };
type IRadius = { [key in Radius]: string };
type IZIndex = { [key in ZIndex]: number };
export interface IColors {
  general: { [key in GeneralColors]: string };
  border: { [key in BorderColors]: string };
  grey: { [key in GreyColors]: string };
  social: { [key in SocialColors]: string };
  table: { [key in TableColors]: string };
  dashboard: { [key in DashboardColors]: string };
  chip: { [key in ChipColors]: { background: string; color: string } };
}

export interface Theme {
  font: IFont;
  screen: IScreen;
  spacing: ISpacing;
  shadows: IShadows;
  radius: IRadius;
  zIndex: IZIndex;
  colors: IColors;
}

// Look & feel after modern-sea-portal (docs/FRONTEND.md), keeping PYA's hues
// (darkened where needed so text and controls meet WCAG 2.1 AA, 4.5:1):
// square corners, thin lines for structure, shadows only on things that float,
// Public Sans with a compact type scale.
const theme: Theme = {
  font: {
    family: "'Public Sans Variable', 'Public Sans', system-ui, -apple-system, 'Segoe UI', sans-serif",
    weight: {
      light: '300',
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
    },
    size: {
      tiny: '12px',
      xxs: '13px',
      xs: '14px',
      sm: '15px',
      md: '17px',
      lg: '19px',
      xl: '22px',
      xxl: '28px',
    },
  },

  colors: {
    general: {
      primary: '#0B5FD9',
      secondary: '#C62828',
      success: '#007A70',
      warning: '#9A4B0E',
      info: '#3459C9',
      error: '#C62828',
      link: '#0B5FD9',
      body: '#F7F9FC',
      white: '#fff',
      disabled: '#CDD0D4',
      text: '#3E4255',
      textSecondary: '#4B4F5C',
      placeholder: '#6B6E85',
      transparent: 'transparent',
      black: '#0B0E2C',
      purple: '#6610f2',
      logo: '#223267',
      sst: '#ddeef8',
      sstTitle: '#22689E',
    },
    border: {
      light: '#f5f5f5',
      main: '#e0e0e0',
      dark: '#bdbdbd',
      pya: '#E9EAF3',
      sst: '#c6d5dc',
    },
    dashboard: {
      icon: '#F4F7FF',
    },
    social: {
      facebook: '#385898',
      twitter: '#1DA1F2',
      linkedIn: '#2867B2',
      github: '#171515',
    },
    table: {
      text: '#3E4255',
    },
    chip: {
      green: { background: '#E4FAED', color: '#0B6E45' },
      yellow: { background: '#FFF3E4', color: '#9A4B0E' },
      red: { background: '#FFEFF0', color: '#B3201F' },
      gray: { background: '#F2F3F5', color: '#1C1E21' },
      info: { background: '#ecf0fc', color: '#2F52BD' },
    },
    grey: {
      5: '#F2F3F5',
      10: '#EBEDF0',
      20: '#DADDE1',
      30: '#CCD0D5',
      40: '#BEC3C9',
      50: '#6A717B',
      60: '#606770',
      70: '#444950',
      80: '#303338',
      90: '#1C1E21',
    },
  },

  // Only floating things (menus, dialogs, toasts) cast a shadow.
  shadows: {
    sm: 'none',
    md: '0 16px 40px rgba(11, 14, 44, 0.16)',
    lg: '0 16px 40px rgba(11, 14, 44, 0.16)',
    xl: '0 24px 64px rgba(11, 14, 44, 0.35)',
    pya: 'none',
    input: 'none',
    table: 'none',
  },

  screen: {
    none: '0px',
    xs: '540px',
    sm: '640px',
    md: '840px',
    lg: '1024px',
    xl: '1280px',
    xxl: '1440px',
    xxxl: '1920px',
  },

  spacing: {
    none: '0px',
    xxs: '5px',
    xs: '14px',
    sm: '20px',
    base: '24px',
    md: '30px',
    lg: '45px',
    xl: '60px',
    xxl: '80px',
  },

  // Square corners everywhere.
  radius: {
    none: '0',
    sm: '0',
    md: '0',
    lg: '0',
    xl: '0',
    xxl: '0',
    full: '0',
    default: '0',
  },

  zIndex: {
    xs: 10,
    sm: 20,
    md: 30,
    lg: 40,
    xl: 50,
    xxl: 60,
    alert: 1000, // Specific z-index for alerts
  },
};

export default theme;
